import React, { useId, useRef, useState, useMemo, useLayoutEffect, useEffect, memo } from 'react';
import cx from 'classnames';
import { useClientSize, useEvent } from '@anton.bobrov/react-hooks';
import { vevet } from '@anton.bobrov/vevet-init';
import { OptionsGrid } from './OptionsList/OptionsGrid/index.js';
import { Thumb } from './Thumb/index.js';
import { useOptions } from './utils/useOptions.js';
import { useFormSelectExpandNavigation } from './utils/useFormSelectExpandNavigation.js';
import { useFormSelectOptionNavigation } from './utils/useFormSelectOptionNavigation.js';
import styles from './index.module.scss.js';
import { Search } from './Search/index.js';
import { useLockBodyScroll } from '../../../utils/hooks/useLockBodyScroll.js';
import { SearchInput } from '../Search/Input/index.js';
import { BasePopup } from '../../ui/popups/Popup/BasePopup/index.js';
import { WithCustomScrollbar } from '../../common/WithCustomScrollbar/index.js';

const MemoThumb = memo(Thumb);
const Select = ({
  className,
  style,
  version = "v1",
  options: optionsProp,
  selectedIds,
  onSelect,
  renderOption,
  renderThumb,
  isMultiple = false,
  hasSearch = false,
  placeholder = "Select an option",
  hasThumbText = true,
  horizontalAlignment = "center",
  verticalAlignment = "auto",
  popupStyle,
  popupGap = 2,
  thumbKind = "button",
  renderOptionsList: CustomOptionsList = null,
  theme = "dark",
  searchPlaceholder = "Search",
  arrow = false,
  searchClearButton,
  checkboxType,
  popupClassName,
  clearChildrenWrapper,
  optionsBorderTop,
  children,
  text,
  position,
  isCenter
}) => {
  const thumbId = useId();
  const listboxId = useId();
  const optionId = useId();
  const ref = useRef(null);
  const [layoutContainerElement, setLayoutContainerEmement] = useState(null);
  const [listboxElement, setListboxElement] = useState(
    null
  );
  const [searchValue, setSearchValue] = useState("");
  const [isActive, setIsActive] = useState(false);
  const [focusedId, setFocusedId] = useState(null);
  const { filteredList, filteredOptions, selectedOptions, allOptions } = useOptions({
    options: optionsProp,
    searchValue,
    selectedIds
  });
  const selectedOptionsName = selectedOptions.map(({ shortName, name }) => shortName || name).join(", ");
  const selectedOptionsValue = selectedOptions.map(({ value }) => value).join(", ");
  const thumbText = selectedOptionsName || placeholder;
  const thumbValue = selectedOptionsValue;
  const hasIcons = !!allOptions.find((option) => "icon" in option);
  const { clientWidth } = useClientSize(ref);
  const handleSelect = useEvent((id) => {
    if (isMultiple) {
      if (selectedIds.includes(id)) {
        onSelect(selectedIds.filter((selectedId) => selectedId !== id));
      } else {
        onSelect([...selectedIds, id]);
      }
    } else {
      onSelect([id]);
      setIsActive(false);
      refPopup?.current?.close();
    }
  });
  useFormSelectExpandNavigation({
    ref,
    listboxElement,
    isActive,
    onActive: setIsActive
  });
  useFormSelectOptionNavigation({
    ref,
    options: filteredOptions,
    isActive,
    onSelect: handleSelect,
    onFocus: setFocusedId,
    focusedId
  });
  const optionKind = useMemo(() => {
    if (version === "v1") {
      return hasIcons ? "v1_with_icon" : "v1_no_icon";
    }
    return version;
  }, [hasIcons, version]);
  const [ready, setReady] = useState(false);
  useLayoutEffect(() => {
    if (isActive && ref.current && listboxElement) {
      setReady(true);
    }
  }, [isActive, ref.current, listboxElement]);
  const [hovered, setHovered] = useState(false);
  useLockBodyScroll(hovered);
  useEffect(() => {
    setHovered(false);
  }, [isActive]);
  const refPopup = useRef(null);
  const [isActviePopup, setIsActivePoup] = useState(false);
  const [maxHeight, setMaxHeight] = useState();
  return /* @__PURE__ */ React.createElement(
    "div",
    {
      ref,
      className: cx(styles.box, "select-box", className, {
        [styles.active]: isActviePopup
      }),
      style: { "--max-height": maxHeight }
    },
    /* @__PURE__ */ React.createElement(
      BasePopup,
      {
        refPopup,
        setIsActive: setIsActivePoup,
        setHeight: setMaxHeight,
        anchor: renderThumb ? renderThumb({
          text: hasThumbText ? thumbText : void 0,
          value: thumbValue,
          image: selectedOptions.length > 0 ? selectedOptions[0].icon : void 0,
          id: thumbId,
          optionId: selectedOptions?.[0]?.id,
          "aria-controls": listboxId,
          "aria-expanded": isActive,
          role: "combobox",
          "aria-haspopup": "listbox",
          "aria-activedescendant": focusedId ? `${optionId}-${focusedId}` : ""
        }) : /* @__PURE__ */ React.createElement(
          MemoThumb,
          {
            kind: thumbKind,
            text: hasThumbText ? thumbText : void 0,
            value: thumbValue,
            image: selectedOptions.length > 0 ? selectedOptions[0].icon : void 0,
            id: thumbId,
            "aria-controls": listboxId,
            "aria-expanded": isActive,
            role: "combobox",
            "aria-haspopup": "listbox",
            "aria-activedescendant": focusedId ? `${optionId}-${focusedId}` : "",
            componentTheme: theme
          }
        ),
        isCenter,
        style: { minWidth: clientWidth, ...popupStyle, zIndex: 99999 },
        gap: popupGap,
        horizontalAlignment,
        position,
        verticalAlignment,
        kind: "none",
        spacing: "none",
        className: cx(styles.popup, popupClassName, {
          [styles["clear-wrapper"]]: clearChildrenWrapper
        }),
        onActive: setIsActive,
        isActive
      },
      arrow && /* @__PURE__ */ React.createElement(
        "div",
        {
          className: cx(styles.arrow, {
            [styles[theme]]: !!theme
          })
        }
      ),
      /* @__PURE__ */ React.createElement(
        "div",
        {
          className: cx(styles.box__inner, {
            [styles[theme]]: !!theme,
            [styles["with-children"]]: !!children
          }),
          onMouseEnter: () => setHovered(true),
          onMouseLeave: () => setHovered(false),
          ref: setLayoutContainerEmement,
          style: { maxHeight: maxHeight ? `${maxHeight}px` : void 0 }
        },
        /* @__PURE__ */ React.createElement(
          WithCustomScrollbar,
          {
            className: styles.scrollbar,
            updateKey: maxHeight
          },
          /* @__PURE__ */ React.createElement(
            "div",
            {
              className: cx(styles["select-wrapper"], "select__wrapper", {
                [styles[version]]: !!version,
                [styles[theme]]: !!theme,
                [styles["no-search"]]: !hasSearch,
                [styles["option-full"]]: !hasIcons && version === "v1"
              })
            },
            hasSearch && // eslint-disable-next-line react/jsx-no-useless-fragment
            /* @__PURE__ */ React.createElement(React.Fragment, null, version === "v1" ? /* @__PURE__ */ React.createElement(
              Search,
              {
                value: searchValue,
                onChange: setSearchValue,
                placeholder: searchPlaceholder,
                theme,
                className: cx(styles["search-v1"], "select__search")
              }
            ) : version === "v2" ? /* @__PURE__ */ React.createElement(
              SearchInput,
              {
                value: searchValue,
                onChange: (event) => setSearchValue(event.target.value),
                onSubmit: () => {
                },
                placeholder: searchPlaceholder,
                inputTheme: theme === "blue" ? "blue-light" : theme,
                className: cx(styles["search-v2"], "select__search")
              }
            ) : /* @__PURE__ */ React.createElement(
              SearchInput,
              {
                value: searchValue,
                onChange: (event) => setSearchValue(event.target.value),
                onSubmit: () => {
                },
                clearButton: searchClearButton,
                placeholder: searchPlaceholder,
                inputTheme: theme === "blue" ? "blue-light" : theme,
                className: cx(styles["search-v3"], "select__search")
              }
            )),
            CustomOptionsList ? /* @__PURE__ */ React.createElement(
              CustomOptionsList,
              {
                ref: setListboxElement,
                listboxId,
                placeholder,
                isMultiple,
                optionId,
                searchedList: filteredList,
                list: optionsProp,
                selectedIds,
                vevet,
                isActive,
                setFocusedId,
                handleSelect,
                optionKind,
                focusedId,
                theme,
                text
              }
            ) : filteredList.length > 0 ? /* @__PURE__ */ React.createElement(
              OptionsGrid,
              {
                innerRef: setListboxElement,
                listboxId,
                placeholder,
                isMultiple,
                optionId,
                list: filteredList,
                selectedIds,
                vevet,
                isActive,
                setFocusedId,
                handleSelect,
                optionKind,
                focusedId,
                theme,
                checkboxType,
                optionsBorderTop,
                renderOption,
                text
              }
            ) : /* @__PURE__ */ React.createElement("p", { className: cx(styles["no-results"], "select__no-results") }, "No results for this query")
          )
        ),
        children && /* @__PURE__ */ React.createElement(
          "div",
          {
            className: cx(
              styles["children-container"],
              "select__children-container"
            )
          },
          children
        )
      )
    )
  );
};
Select.displayName = "Form.Select";

export { Select };
//# sourceMappingURL=index.js.map
